/**
 * Constantes y utilidades de presentación del área de Contabilidad:
 * pestañas, permisos, nombres de archivo y formato de saldos deudores y
 * acreedores.
 */
import type { Permission } from '@tumin/domain'
import { formatMonth } from '@/lib/dates'
import { formatValue } from '@/lib/format'

/** Pestañas del área de Contabilidad (`?tab=`). */
export const ACCOUNTING_TABS = ['journal', 'trial-balance', 'ledger', 'periods'] as const
/** Pestaña del área de Contabilidad. */
export type AccountingTab = (typeof ACCOUNTING_TABS)[number]
/** Pestaña que se abre por defecto. */
export const DEFAULT_ACCOUNTING_TAB: AccountingTab = 'journal'

/** Nombre en español de cada pestaña. */
export const ACCOUNTING_TAB_LABELS: Record<AccountingTab, string> = {
  journal: 'Libro diario',
  'trial-balance': 'Balanza de comprobación',
  ledger: 'Libro mayor',
  periods: 'Períodos y cierre',
}

/** Permiso para cerrar períodos, correr ajustes y mapear cuentas. */
export const ACCOUNTING_MANAGE: Permission = 'accounting:manage'
/** Permiso para reabrir un período cerrado. */
export const ACCOUNTING_REOPEN: Permission = 'accounting:reopen'
/** Permiso para exportar archivos (balanza, diario). */
export const EXPORT_PERMISSION: Permission = 'reports:export'

/** Nombre base de los archivos exportados. */
export const EXPORT_FILE_BASES = { journal: 'libro-diario', trialBalance: 'balanza-de-comprobacion' } as const
/** Extensión de los archivos CSV. */
export const CSV_EXTENSION = '.csv'
/** Valor del parámetro `format` para pedir CSV al API. */
export const CSV_FORMAT = 'csv'

/** Sufijos de saldo: deudor (D) y acreedor (A), como en las balanzas guatemaltecas. */
const BALANCE_SIDE = { debit: 'D', credit: 'A' } as const

/**
 * Saldo contable legible: el valor absoluto con "D" si es deudor (debe >
 * haber) o "A" si es acreedor. Cero no lleva sufijo.
 */
export function formatBalance(balance: number): string {
  if (balance === 0) return formatValue(0, 'money')
  return `${formatValue(Math.abs(balance), 'money')} ${balance > 0 ? BALANCE_SIDE.debit : BALANCE_SIDE.credit}`
}

/** Largo de "AAAA-MM" dentro de la fecha de inicio de un período. */
const YEAR_MONTH_LENGTH = 7

/** Mes de un período (AAAA-MM-01) legible, p. ej. "abr 2026". */
export function periodLabel(periodStart: string): string {
  return formatMonth(periodStart.slice(0, YEAR_MONTH_LENGTH))
}
